import { useState, useCallback } from "react";
import { ShieldCheck, Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import {
  useRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
} from "@/hooks/admin/useOperations";
import type { Role, RolePermission, CreateRoleInput } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RESOURCES = [
  { key: "orders", label: "Commandes" },
  { key: "products", label: "Produits" },
  { key: "customers", label: "Clients" },
  { key: "payments", label: "Paiements" },
  { key: "deliveries", label: "Livraisons" },
  { key: "inventory", label: "Stock" },
  { key: "reports", label: "Rapports" },
  { key: "settings", label: "Paramètres" },
];

const ACTIONS = ["read", "write", "delete"];

// ---------------------------------------------------------------------------
// Permissions editor
// ---------------------------------------------------------------------------

interface PermissionsEditorProps {
  readonly permissions: readonly RolePermission[];
  readonly onChange: (permissions: RolePermission[]) => void;
}

function PermissionsEditor({ permissions, onChange }: PermissionsEditorProps) {
  const hasPermission = useCallback(
    (resource: string, action: string) => {
      const rp = permissions.find((p) => p.resource === resource);
      return rp ? rp.actions.includes(action) : false;
    },
    [permissions],
  );

  const toggle = useCallback(
    (resource: string, action: string) => {
      const existing = permissions.find((p) => p.resource === resource);
      if (existing) {
        const nextActions = existing.actions.includes(action)
          ? existing.actions.filter((a) => a !== action)
          : [...existing.actions, action];

        const next: RolePermission[] = nextActions.length === 0
          ? (permissions.filter((p) => p.resource !== resource) as RolePermission[])
          : (permissions.map((p) =>
              p.resource === resource ? { resource, actions: nextActions } : p,
            ) as RolePermission[]);
        onChange(next);
      } else {
        onChange([...(permissions as RolePermission[]), { resource, actions: [action] }]);
      }
    },
    [permissions, onChange],
  );

  return (
    <div className="space-y-3 rounded-md border border-stone-200 p-3">
      {RESOURCES.map((resource) => (
        <div key={resource.key}>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-stone-500">
            {resource.label}
          </p>
          <div className="flex flex-wrap gap-3">
            {ACTIONS.map((action) => (
              <label
                key={action}
                className="flex cursor-pointer items-center gap-1.5 text-xs text-stone-600"
              >
                <input
                  type="checkbox"
                  checked={hasPermission(resource.key, action)}
                  onChange={() => toggle(resource.key, action)}
                  className="rounded border-stone-300"
                />
                {action}
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Role form modal
// ---------------------------------------------------------------------------

interface RoleFormModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly isSaving: boolean;
  readonly onSave: (input: CreateRoleInput) => Promise<void>;
  readonly initial?: Role;
}

function RoleFormModal({ open, onClose, isSaving, onSave, initial }: RoleFormModalProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [permissions, setPermissions] = useState<RolePermission[]>(
    initial ? [...initial.permissions] : [],
  );
  const [error, setError] = useState<string | null>(null);

  const handleClose = useCallback(() => {
    if (!initial) {
      setName("");
      setLabel("");
      setDescription("");
      setPermissions([]);
    }
    setError(null);
    onClose();
  }, [onClose, initial]);

  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      setError("Le nom technique est requis.");
      return;
    }
    if (!label.trim()) {
      setError("Le libellé est requis.");
      return;
    }
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        label: label.trim(),
        description: description.trim(),
        permissions,
      });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Une erreur est survenue.");
    }
  }, [name, label, description, permissions, onSave, handleClose]);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={initial ? `Modifier — ${initial.label}` : "Nouveau rôle"}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={isSaving}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Nom technique <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!!initial?.is_system}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100 disabled:bg-stone-50 disabled:text-stone-400"
              placeholder="custom_role"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-stone-700">
              Libellé <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
              placeholder="Rôle personnalisé"
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-stone-700">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
            placeholder="Description du rôle…"
          />
        </div>

        <div>
          <p className="mb-2 text-sm font-medium text-stone-700">Permissions</p>
          <PermissionsEditor permissions={permissions} onChange={setPermissions} />
        </div>
      </div>
    </AdminModal>
  );
}

// ---------------------------------------------------------------------------
// Role card
// ---------------------------------------------------------------------------

interface RoleCardProps {
  readonly role: Role;
  readonly onEdit: (role: Role) => void;
  readonly onDelete: (id: string) => void;
}

function RoleCard({ role, onEdit, onDelete }: RoleCardProps) {
  const permissionSummary = (role.permissions ?? [])
    .slice(0, 3)
    .map((p) => `${p.resource} (${(p.actions ?? []).join(", ")})`)
    .join(" · ");

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-semibold text-stone-900">{role.label}</h3>
            {role.is_system && (
              <span className="inline-flex items-center rounded-full border border-stone-200 bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">
                Système
              </span>
            )}
          </div>
          <p className="mt-0.5 font-mono text-xs text-stone-400">{role.name}</p>
          {role.description && (
            <p className="mt-1.5 text-sm text-stone-600">{role.description}</p>
          )}
          {permissionSummary && (
            <p className="mt-2 text-xs leading-relaxed text-stone-400">
              {permissionSummary}
              {role.permissions.length > 3 && ` · +${role.permissions.length - 3} autres`}
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-stone-500">
            {role.user_count} utilisateur{role.user_count !== 1 ? "s" : ""}
          </span>
          <button
            onClick={() => onEdit(role)}
            className="rounded p-1.5 text-stone-400 hover:bg-stone-100 hover:text-stone-700"
            title="Modifier"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => !role.is_system && onDelete(role.id)}
            disabled={role.is_system}
            title={role.is_system ? "Rôle système — non supprimable" : "Supprimer"}
            className="rounded p-1.5 text-stone-400 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Roles() {
  const [showCreate, setShowCreate] = useState(false);
  const [editRole, setEditRole] = useState<Role | null>(null);

  const { data, isLoading } = useRoles();
  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();

  const roles = data?.roles ?? [];

  const handleCreate = useCallback(
    async (input: CreateRoleInput) => {
      await createMutation.mutateAsync(input);
      setShowCreate(false);
    },
    [createMutation],
  );

  const handleUpdate = useCallback(
    async (input: CreateRoleInput) => {
      if (!editRole) return;
      await updateMutation.mutateAsync({ id: editRole.id, input });
      setEditRole(null);
    },
    [editRole, updateMutation],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (!confirm("Supprimer ce rôle ?")) return;
      await deleteMutation.mutateAsync(id);
    },
    [deleteMutation],
  );

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Rôles et permissions
              </h1>
              <p className="text-sm text-stone-500">
                {roles.length} rôle{roles.length !== 1 ? "s" : ""} configuré
                {roles.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Nouveau rôle
          </Button>
        </div>

        {/* Role cards */}
        {isLoading ? (
          <div className="grid gap-4 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-32 animate-pulse rounded-lg border border-stone-200 bg-stone-50"
              />
            ))}
          </div>
        ) : roles.length === 0 ? (
          <div className="rounded-lg border border-stone-200 bg-white py-12 text-center">
            <p className="text-sm text-stone-400">Aucun rôle configuré.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {roles.map((role) => (
              <RoleCard
                key={role.id}
                role={role}
                onEdit={setEditRole}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      <RoleFormModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        isSaving={createMutation.isPending}
        onSave={handleCreate}
      />

      {/* Edit modal */}
      {editRole && (
        <RoleFormModal
          open={!!editRole}
          onClose={() => setEditRole(null)}
          isSaving={updateMutation.isPending}
          onSave={handleUpdate}
          initial={editRole}
        />
      )}
    </AdminLayout>
  );
}
